import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useEffect, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { firebaseAuth } from './lib/firebase';
import { api } from './lib/api';
import type { ApiSuccess } from './lib/api';

// Pages
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { Dashboard } from './pages/Dashboard';
import { Pricing } from './pages/Pricing';
import { Blog } from './pages/Blog';
import { BlogPost } from './pages/BlogPost';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { Settings } from './pages/Settings';
import { NewBook } from './pages/NewBook';
import { BookDetail } from './pages/BookDetail';
import { BookEditor } from './pages/BookEditor';
import { KdpWalkthrough } from './pages/KdpWalkthrough';
import { AuthorPersonaPage } from './pages/AuthorPersona';
import { SeriesPage } from './pages/Series';
import { SeriesDetail } from './pages/SeriesDetail';
import { CoverStudio } from './pages/CoverStudio';
import { PersonaSetup } from './pages/PersonaSetup';

// Components
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { useAnalytics } from './hooks/useAnalytics';

interface UserMeResponse {
  booksCreated: number;
}

const NEW_ACCOUNT_WINDOW_MS = 60_000;

export default function App() {
  const { setUser, setLoading } = useAuthStore();
  const navigate = useNavigate();
  const redirectChecked = useRef<Set<string>>(new Set());

  // GA4 page_view on every route change; no-op when measurementId not set
  useAnalytics();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      setUser(user);
      setLoading(false);

      if (user) {
        // Only run the new-user redirect check once per auth session per uid
        if (!redirectChecked.current.has(user.uid)) {
          redirectChecked.current.add(user.uid);

          void (async () => {
            try {
              const res = await api.get<ApiSuccess<UserMeResponse>>('/users/me');
              const { booksCreated } = res.data.data;

              const creationTime = user.metadata.creationTime
                ? new Date(user.metadata.creationTime).getTime()
                : null;
              const isNewAccount =
                creationTime !== null &&
                Date.now() - creationTime < NEW_ACCOUNT_WINDOW_MS;

              if (booksCreated === 0 && isNewAccount) {
                navigate('/books/new', { replace: true });
              }
            } catch {
              // Non-fatal — user doc may not exist yet on very first load
            }
          })();
        }
      } else {
        // User signed out — reset so next login re-checks
        redirectChecked.current.clear();
      }
    });
    return unsubscribe;
  }, [setUser, setLoading, navigate]);

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/kdp-walkthrough" element={<KdpWalkthrough />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      {/* Protected */}
      <Route element={<ProtectedRoute />}>
        <Route path="/persona-setup" element={<PersonaSetup />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/books/new" element={<NewBook />} />
        <Route path="/books/:bookId" element={<BookDetail />} />
        <Route path="/books/:bookId/editor" element={<BookEditor />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/persona" element={<AuthorPersonaPage />} />
        <Route path="/series" element={<SeriesPage />} />
        <Route path="/series/:seriesId" element={<SeriesDetail />} />
        <Route path="/books/:bookId/covers" element={<CoverStudio />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
